import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import Panel from '../Panel';
import CopySection, { FinishStepButton } from '../CopySection';
import styles from './HowDidYouHearPanel.module.scss';
import { apiRequest } from '@support/api';

export type ClayUserReference = { key: string; value: string };

export default function HowDidYouHearPanel({
  clayUserReferences,
  proceedToNextStep,
}: {
  clayUserReferences: ClayUserReference[];
  proceedToNextStep: () => void;
}) {
  const [infoSource, setInfoSource] = useState<string>(undefined);
  const [otherInfoSource, setOtherInfoSource] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const submitInfoSource = () => {
    let value = undefined;

    if (infoSource === 'other') {
      value = otherInfoSource;
    }

    setSubmitting(true);
    apiRequest<{
      reference: string;
      value?: string;
    }>(
      'POST',
      '/onboarding/reference/',
      { reference: infoSource, value },
      { apiVersion: 'v2' },
    ).finally(() => {
      setSubmitting(false);
      proceedToNextStep();
    });
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !!otherInfoSource) {
      submitInfoSource();
    }

    if (e.key === 'Escape' && otherInfoSource.length !== 0) {
      e.stopPropagation();
      setOtherInfoSource('');
    }
  };

  useEffect(() => {
    if (infoSource === 'other') {
      inputRef.current.focus();
    }
  }, [infoSource]);

  useEffect(() => {
    if (clayUserReferences.length === 0) {
      proceedToNextStep();
    }
  }, []);

  return (
    <Panel>
      <CopySection>
        <h2>How did you hear about us?</h2>
        <p>
          Answering this question really helps us get a sense of how to serve you better. Thank you!
        </p>
        <FinishStepButton
          onClick={submitInfoSource}
          disabled={!infoSource || (infoSource === 'other' && !otherInfoSource)}
          loading={submitting}
          disabledMsg="Make a selection to continue"
        >
          Continue
        </FinishStepButton>
      </CopySection>
      <div className={styles.infoSourceOptions}>
        {clayUserReferences.map(({ key, value }) => (
          <button
            key={key}
            className={cn(styles.option, infoSource === key && styles.selected)}
            onClick={() => setInfoSource(key)}
          >
            {value}
          </button>
        ))}
        <input
          ref={inputRef}
          className={cn(styles.otherInput, infoSource === 'other' && styles.active)}
          type="text"
          placeholder="Please specify"
          value={otherInfoSource}
          maxLength={250}
          onChange={(e) => setOtherInfoSource(e.target.value)}
          onKeyDown={handleInputKeyDown}
        />
      </div>
    </Panel>
  );
}
